// React
import React, {Component, Fragment} from 'react';

// Third party
import {inject, observer} from 'mobx-react';

// Bulma
import {Button, Content} from 'react-bulma-components';
import {Paper, Typography, Stack} from '@wellstone-solutions/web';

// WS
import TutorialDiv from './tutorial/tutorialdiv';
import {
  PfBarChart,
  PfChartLegend,
  PfCheckinChart,
  HabitsChart,
} from 'modules/activity';
import {LoadingIndicator} from 'components/shared/LoadingIndicator';
import {Palette} from '../palette';
import {getBoolVariation, LD_FLAGS} from 'modules/feature-flags';
import {Icon, IconNames} from '@wellstone-solutions/web';
import ScrollTo from './tutorial/scrollto';

class MembersOverview extends Component {
  state = {
    activityDataPanelOpen: true,
    habitsPanelOpen: false,
    checkinPanelOpen: false,
  };

  constructor(props) {
    super(props);
    const {me} = this.props.meStore;
    // Read once at construction — matches prior behavior. Reactivity loss is
    // acceptable; the flag won't change for a given session.
    this.chartRole = getBoolVariation(LD_FLAGS.PATHFINDER_DATA_ACCESS)
      ? 'pathfinder'
      : me.membership.role;
  }

  componentDidMount() {}

  updateChart = (labels, type) => {
    this.setState({[type]: labels});
  };

  _togglePanel = (panel, force) => {
    const panelState = force ? force.state : !this.state[panel + 'PanelOpen'];
    this.setState({
      [panel + 'PanelOpen']: panelState,
    });
  };

  _showDataChart = (type, title) => {
    if (
      !this.props[type] ||
      !this.props[type].legend ||
      !this.props[type].series
    ) {
      return null;
    }

    return (
      <TutorialDiv id="member-overview-data-chart">
        <Paper style={styles.box}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={styles.cardHeader}>
            <Typography style={styles.cardHeaderText} variant="h5">
              {title}
            </Typography>

            <Button
              onClick={() => this._togglePanel(type)}
              style={styles.expandButton}>
              <Icon
                name={
                  this.state[type + 'PanelOpen']
                    ? IconNames.Minus
                    : IconNames.Plus
                }
                color="#FFF"
              />
            </Button>
          </Stack>

          {this.state[type + 'PanelOpen'] ? (
            <Content style={styles.chartContainer}>
              <PfChartLegend
                updateChart={this.updateChart}
                chartType={type}
                labels={this.props[type].legend}
              />
              <PfBarChart
                data={this.props[type].series}
                x={{key: this.props.aggregator, type: 'category'}}
                orientation="horizontal"
                selectedValues={this.state[type] || this.props[type].legend}
                size={{height: 400, width: '100%'}}
                showDetailsModal={this.props.showDetailsModal ?? true}
              />
            </Content>
          ) : null}
        </Paper>
      </TutorialDiv>
    );
  };

  _hideAllPanels = (cb) => {
    this.setState(
      {
        ['activityDataPanelOpen']: false,
        ['badgeDataPanelOpen']: false,
        ['appnavigationDataPanelOpen']: false,
        ['engagementDataPanelOpen']: false,
        ['checkinPanelOpen']: false,
        ['habitsPanelOpen']: false,
      },
      cb,
    );
  };

  _showHabitsChart = () => {
    if (!this.props.habitData) {
      return <LoadingIndicator />;
    } else if (this.props.habitData.length === 0) {
      return null;
    }

    return (
      <TutorialDiv
        id="member-overview-habits-chart"
        tutorialFunction={async () => {
          this._hideAllPanels(() => this._togglePanel('habits', {state: true}));
        }}>
        <ScrollTo stepId="habits" />
        <Paper style={styles.box}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={styles.cardHeader}>
            <Typography variant="h5" style={styles.cardHeaderText}>
              Habits
            </Typography>

            <Button
              onClick={() => this._togglePanel('habits')}
              style={styles.expandButton}>
              <Icon
                name={
                  this.state.habitsPanelOpen ? IconNames.Minus : IconNames.Plus
                }
                color="#FFF"
              />
            </Button>
          </Stack>

          {this.state.habitsPanelOpen ? (
            <Content style={styles.chartContainer}>
              <HabitsChart
                aggregator={this.props.aggregator}
                habitData={this.props.habitData}
              />
            </Content>
          ) : null}
        </Paper>
      </TutorialDiv>
    );
  };

  _showCheckinChart = () => {
    if (!this.props.checkinData) {
      return <LoadingIndicator />;
    } else if (this.props.checkinData.length === 0) {
      return null;
    }

    return (
      <TutorialDiv
        id="member-overview-checkin-chart"
        tutorialFunction={() => {
          this._hideAllPanels(() =>
            this._togglePanel('checkin', {state: true}),
          );
        }}>
        <ScrollTo stepId="checkins" />
        <Paper style={styles.box}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={styles.cardHeader}>
            <Typography variant="h5" style={styles.cardHeaderText}>
              Check ins
            </Typography>

            <Button
              onClick={() => this._togglePanel('checkin')}
              style={styles.expandButton}>
              <Icon
                name={
                  this.state.checkinPanelOpen ? IconNames.Minus : IconNames.Plus
                }
                color="#FFF"
              />
            </Button>
          </Stack>

          {this.state.checkinPanelOpen ? (
            <Content style={styles.chartContainer}>
              <PfCheckinChart
                aggregator={this.props.aggregator}
                checkinData={this.props.checkinData}
                showDetailsModal={this.props.showDetailsModal ?? true}
              />
            </Content>
          ) : null}
        </Paper>
      </TutorialDiv>
    );
  };

  render() {
    return (
      <Fragment>
        {this._showDataChart('activityData', 'Basic Activity')}
        {this._showHabitsChart()}
        {this._showCheckinChart()}
        {this.chartRole === 'pathfinder'
          ? this._showDataChart('staffData', 'Staff Events')
          : null}
        {this.chartRole === 'pathfinder'
          ? this._showDataChart('badgeData', 'Badge Earning')
          : null}
        {this.chartRole === 'pathfinder'
          ? this._showDataChart('appnavigationData', 'Navigation')
          : null}
        {this.chartRole === 'pathfinder'
          ? this._showDataChart('engagementData', 'Engagement')
          : null}
        {/*{this._showEvalCard()}*/}
      </Fragment>
    );
  }
}

const styles = {
  box: {
    marginBottom: 20,
    padding: 0,
    overflow: 'hidden',
  },
  cardHeader: {
    backgroundColor: Palette.DARK_INFO,
    paddingTop: '10px',
    paddingLeft: '20px',
    paddingBottom: '10px',
    paddingRight: '20px',
    width: '100%',
  },
  cardHeaderText: {
    color: 'white',
  },
  chartContainer: {
    paddingLeft: 20,
    paddingRight: 20,
    paddingTop: 20,
    paddingBottom: 20,
  },
  expandButton: {
    fontSize: 20,
    fontWeight: 'bold',
    background: 'transparent',
    borderWidth: 0,
    height: 50,
    paddingTop: 6,
    color: 'white',
    padding: 10,
  },
};

export default inject('meStore')(observer(MembersOverview));
